import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import SessionHistoryList from '../components/session/SessionHistoryList';
import { getTutorSessionHistory, SessionHistoryItem } from '../services/tutorSessionHistoryService';
import {
  AccountTree as StructureIcon,
  VpnKey as CodesIcon,
  People as RosterIcon,
  Quiz as TestsIcon,
  Assignment as AssignmentIcon,
  Archive as ArchiveIcon,
  PublishedWithChanges as PublishIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarMonth as ScheduleIcon,
  PlayCircle as StartSessionIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  archiveCourse,
  getCourse,
  getTutorCourseMetricsSummary,
  getTutorCourseTopicMetrics,
  publishCourse,
  TutorCourse,
  TutorCourseSummaryMetrics,
  TutorCourseTopicMetrics,
} from '../services/tutorCourseService';

const statusColor = (status: string) => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'DRAFT') return 'default';
  return 'warning';
};

const TutorCourseDashboard: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const id = Number(courseId);

  const [course, setCourse] = useState<TutorCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const [metrics, setMetrics] = useState<TutorCourseSummaryMetrics | null>(null);
  const [topicMetrics, setTopicMetrics] = useState<TutorCourseTopicMetrics[] | null>(null);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryItem[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      getCourse(id),
      getTutorCourseMetricsSummary(id).catch(() => null),
    ]).then(([courseData, metricsData]) => {
      setCourse(courseData);
      setMetrics(metricsData);
    }).catch((err) => setError(err?.response?.data?.message || err.message || 'Failed to load course'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleTopicsExpand = (_: React.SyntheticEvent, expanded: boolean) => {
    if (expanded && topicMetrics === null) {
      setTopicsLoading(true);
      getTutorCourseTopicMetrics(id)
        .then(setTopicMetrics)
        .catch(() => setTopicMetrics([]))
        .finally(() => setTopicsLoading(false));
    }
  };

  const handleHistoryExpand = (_: React.SyntheticEvent, expanded: boolean) => {
    if (expanded && sessionHistory === null) {
      setHistoryLoading(true);
      getTutorSessionHistory(id)
        .then(setSessionHistory)
        .catch(() => setSessionHistory([]))
        .finally(() => setHistoryLoading(false));
    }
  };

  const handlePublish = async () => {
    if (!window.confirm(`Publish "${course?.name}"? This will make it active and allow students to enrol via a registration code.`)) return;
    setPublishing(true);
    try {
      const updated = await publishCourse(id);
      setCourse(updated);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to publish course');
    } finally {
      setPublishing(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm(`Archive "${course?.name}"? Students will lose access, but all data is preserved and the course can be restored.`)) return;
    setArchiving(true);
    try {
      await archiveCourse(id);
      navigate('/tutor/courses');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to archive course');
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !course) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Course not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/tutor/courses')} sx={{ mt: 2 }}>
          Back to My Courses
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Button size="small" startIcon={<BackIcon />} onClick={() => navigate('/tutor/courses')}>
          My Courses
        </Button>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4">{course.name}</Typography>
          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
            <Chip label={course.status} color={statusColor(course.status) as any} size="small" />
            {course.subjectName && <Typography variant="body2" color="text.secondary">{course.subjectName}</Typography>}
            {course.board && <Typography variant="body2" color="text.secondary">· {course.board}</Typography>}
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {course.status === 'ACTIVE' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<StartSessionIcon />}
              onClick={() => navigate(`/tutor/courses/${id}/session`)}
            >
              Start Session
            </Button>
          )}
          {course.status === 'DRAFT' && (
            <Button
              variant="contained"
              color="success"
              startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}
              disabled={publishing}
              onClick={handlePublish}
            >
              {publishing ? 'Publishing…' : 'Publish Course'}
            </Button>
          )}
        </Box>
      </Box>

      {/* ─── Course details ───────────────────────────────────────────────── */}
      <Box display="flex" gap={3} mb={4} flexWrap="wrap">
        {course.enrolledStudentCount != null && (
          <Box>
            <Typography variant="caption" color="text.secondary">Enrolled students</Typography>
            <Typography variant="h5" fontWeight="bold">{course.enrolledStudentCount}</Typography>
          </Box>
        )}
        {course.registrationMethod && (
          <Box>
            <Typography variant="caption" color="text.secondary">Registration</Typography>
            <Typography variant="body1">{course.registrationMethod}</Typography>
          </Box>
        )}
        {course.createdAt && (
          <Box>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body1">{new Date(course.createdAt).toLocaleDateString()}</Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* ─── Summary metrics panel ────────────────────────────────────────── */}
      {metrics && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>Performance Overview</Typography>
          {metrics.totalEnrolled === 0 ? (
            <Typography color="text.secondary">No students enrolled yet.</Typography>
          ) : (
            <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">{metrics.totalEnrolled}</Typography>
                    <Typography variant="caption" color="text.secondary">Enrolled</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">{metrics.activeStudents}</Typography>
                    <Typography variant="caption" color="text.secondary">Active (14d)</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">{metrics.avgCompletionPct.toFixed(0)}%</Typography>
                    <Typography variant="caption" color="text.secondary">Avg completion</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight="bold">{metrics.completedStudents}</Typography>
                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {metrics.atRiskStudents.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {metrics.atRiskStudents.length} student{metrics.atRiskStudents.length > 1 ? 's' : ''} at risk (enrolled &gt;7 days, no activity)
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {metrics.atRiskStudents.map((s) => (
                      <li key={s.studentId}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => navigate(`/tutor/courses/${id}/roster`)}
                          sx={{ p: 0, minWidth: 0, textTransform: 'none', textDecoration: 'underline' }}
                        >
                          {s.displayName}
                        </Button>
                        {' '}({s.daysSinceEnrolment}d ago)
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              <Accordion onChange={handleTopicsExpand}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Per-topic breakdown</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {topicsLoading ? (
                    <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
                  ) : topicMetrics && topicMetrics.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Topic</TableCell>
                            <TableCell align="right">Completed</TableCell>
                            <TableCell align="right">Avg score</TableCell>
                            <TableCell align="right">Pass rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topicMetrics.map((t) => (
                            <TableRow key={t.nodeId}>
                              <TableCell>{t.nodeTitle}</TableCell>
                              <TableCell align="right">{t.completedStudents}</TableCell>
                              <TableCell align="right">{t.avgQuizScore != null ? `${t.avgQuizScore.toFixed(1)}%` : '—'}</TableCell>
                              <TableCell align="right">{t.passRate != null ? `${t.passRate.toFixed(0)}%` : '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary">No topic data available.</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          )}
          <Divider sx={{ mt: 3, mb: 3 }} />
        </Box>
      )}

      {/* ─── Navigation shortcuts ─────────────────────────────────────────── */}
      <Typography variant="h6" gutterBottom>Manage Course</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(`/tutor/courses/${id}/structure`)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <StructureIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">Topic Structure</Typography>
                  <Typography variant="body2" color="text.secondary">Build and organise course topics</Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(`/tutor/courses/${id}/codes`)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CodesIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">Registration Codes</Typography>
                  <Typography variant="body2" color="text.secondary">Generate codes for students to join</Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(`/tutor/courses/${id}/roster`)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <RosterIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">Student Roster</Typography>
                  <Typography variant="body2" color="text.secondary">View and manage enrolled students</Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(`/tutor/courses/${id}/tests`)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TestsIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">Tests</Typography>
                  <Typography variant="body2" color="text.secondary">Schedule and review assessments</Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(`/tutor/courses/${id}/schedule`)}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScheduleIcon color="primary" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">Topic Scheduling</Typography>
                  <Typography variant="body2" color="text.secondary">Set target dates for topic nodes</Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Tooltip title="Coming soon" placement="top">
            <Card variant="outlined" sx={{ opacity: 0.5 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssignmentIcon color="disabled" />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium" color="text.disabled">Assignments</Typography>
                  <Typography variant="body2" color="text.disabled">Set and track student assignments</Typography>
                </Box>
              </CardContent>
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined" sx={{ borderColor: 'error.light' }}>
            <CardActionArea onClick={handleArchive} disabled={archiving}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {archiving ? <CircularProgress size={24} color="error" /> : <ArchiveIcon color="error" />}
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium" color="error">Archive Course</Typography>
                  <Typography variant="body2" color="text.secondary">Suspend access; data is preserved</Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      {/* ─── Session History ──────────────────────────────────────────────── */}
      <Divider sx={{ mt: 4, mb: 2 }} />
      <Accordion onChange={handleHistoryExpand}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Session History</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
          ) : (
            <SessionHistoryList sessions={sessionHistory ?? []} />
          )}
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default TutorCourseDashboard;
